import { API_ASSET_ORIGIN } from "@/config";

declare const require: {
  context: (
    directory: string,
    useSubdirectories?: boolean,
    regExp?: RegExp
  ) => {
    keys(): string[];
    <T>(id: string): T;
  };
};

const ABSOLUTE_URL_PATTERN = /^(?:[a-z][a-z\d+\-.]*:)?\/\//i;
const IMAGE_EXTENSION_PATTERN = /\.(svg|png|jpe?g|webp)$/i;
const API_HOSTED_PREFIXES = ["/assets/", "/dm-suite/assets/", "/storage/"];
const SIDENAV_ICON_PATH_PATTERN =
  /\/(?:storage\/)?dm-suite\/assets\/icons\/side-navigation-icons\/([^/?#]+)$/i;
const LEGACY_SIDENAV_ICON_PATH_PATTERN =
  /\/assets\/icons\/side-navigation-icons\/([^/?#]+)$/i;
const APP_BASE_URL = process.env.BASE_URL || "/";

const buildLocalSidenavIconMap = (): Record<string, string> => {
  if (typeof require.context !== "function") {
    return {};
  }

  const iconContext = require.context(
    "@/assets/icons/side-navigation-icons",
    false,
    /\.(svg|png|jpe?g|webp)$/i
  );

  return iconContext.keys().reduce<Record<string, string>>((map, key) => {
    const filename = key.replace("./", "");
    map[filename] = iconContext<string>(key);
    return map;
  }, {});
};

const LOCAL_SIDENAV_ICON_MAP = buildLocalSidenavIconMap();

const withApiAssetOrigin = (path: string) => {
  const origin = API_ASSET_ORIGIN.replace(/\/+$/, "");
  return `${origin}${path}`;
};

const withAppBaseUrl = (path: string) => {
  const normalizedBaseUrl = APP_BASE_URL.replace(/\/?$/, "/");
  return `${normalizedBaseUrl}${path.replace(/^\/+/, "")}`;
};

const extractSidenavIconFilename = (value: string) => {
  const normalizedValue = value.split("?")[0].split("#")[0];
  const segments = normalizedValue.split("/").filter(Boolean);
  return decodeURIComponent(segments[segments.length - 1] || "");
};

const resolveLocalSidenavIcon = (value: string) => {
  const filename = extractSidenavIconFilename(value);
  if (!filename) {
    return "";
  }

  return LOCAL_SIDENAV_ICON_MAP[filename] || "";
};

const resolveAppHostedSidenavIcon = (value: string) => {
  const filename = extractSidenavIconFilename(value);
  if (!filename) {
    return "";
  }

  return withAppBaseUrl(
    `/assets/icons/side-navigation-icons/${encodeURIComponent(filename)}`
  );
};

export const isSidenavImageIcon = (value: string | null | undefined) => {
  if (!value || typeof value !== "string") return false;

  const icon = value.trim();
  if (!icon) return false;

  // Iconify format (prefix:icon-name) is not an image file
  if (icon.includes(":") && !icon.includes("/") && !IMAGE_EXTENSION_PATTERN.test(icon)) {
    return false;
  }

  if (icon.startsWith("/") || ABSOLUTE_URL_PATTERN.test(icon)) return true;
  if (/^data:image\//i.test(icon)) return true;

  return IMAGE_EXTENSION_PATTERN.test(icon);
};

export const resolveSidenavIconUrl = (value: string | null | undefined) => {
  if (!value || typeof value !== "string") return "";

  const icon = value.trim();
  if (!icon) return "";

  const localIconUrl = resolveLocalSidenavIcon(icon);
  if (localIconUrl) {
    return localIconUrl;
  }

  const directMatch =
    icon.match(SIDENAV_ICON_PATH_PATTERN) ||
    icon.match(LEGACY_SIDENAV_ICON_PATH_PATTERN);
  if (directMatch?.[1]) {
    const filename = decodeURIComponent(directMatch[1]);
    if (LOCAL_SIDENAV_ICON_MAP[filename]) {
      return LOCAL_SIDENAV_ICON_MAP[filename];
    }

    return resolveAppHostedSidenavIcon(filename);
  }

  if (
    icon.startsWith("/assets/icons/side-navigation-icons/") ||
    icon.startsWith("/dm-suite/assets/icons/side-navigation-icons/") ||
    icon.startsWith("/dm-suite/public/assets/icons/side-navigation-icons")
  ) {
    return resolveAppHostedSidenavIcon(icon);
  }

  if (
    ABSOLUTE_URL_PATTERN.test(icon) ||
    /^data:image\//i.test(icon) ||
    /^blob:/i.test(icon)
  ) {
    return icon;
  }

  if (API_HOSTED_PREFIXES.some((prefix) => icon.startsWith(prefix))) {
    return withApiAssetOrigin(icon);
  }

  return icon;
};
